// Twitch API Service using EventSub WebSocket with backend OAuth
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001'
const DEBUG_MODE = import.meta.env.VITE_DEBUG_MODE === 'true'
// Debug tier for testing: '1000' = Tier 1, '2000' = Tier 2, '3000' = Tier 3
const DEBUG_TIER = import.meta.env.VITE_DEBUG_TIER || '3000'

class TwitchApiService {
  constructor() {
    this.ws = null
    this.sessionId = null
    this.connected = false
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.messageCallbacks = []
    this.cheerCallbacks = []
    this.extensionCallbacks = [] // Extension PubSub messages
    this.statusCallbacks = []
    this.subscriberCache = new Map()

    // Auth data from backend
    this.accessToken = null
    this.clientId = null
    this.broadcasterId = null
    this.userId = null
    this.broadcasterLogin = null
    this._intentionalClose = false
  }

  // Set broadcaster context for multi-tenant
  setBroadcasterLogin(login) {
    this.broadcasterLogin = login
  }

  // Fetch auth credentials from backend for specific broadcaster
  async authenticate() {
    if (!this.broadcasterLogin) {
      return { needsAuth: true, authUrl: `${API_BASE}/twitch/terrarium/auth/login`, error: 'No broadcaster specified' }
    }

    try {
      const response = await fetch(`${API_BASE}/twitch/terrarium/api/broadcaster/${this.broadcasterLogin}/token`, {
        credentials: 'include'
      })

      if (!response.ok) {
        const data = await response.json()
        if (data.authUrl || response.status === 404) {
          this.updateStatus('Authentication required')
          return { needsAuth: true, authUrl: `${API_BASE}/twitch/terrarium/auth/login` }
        }
        throw new Error(data.error || 'Authentication failed')
      }

      const data = await response.json()
      this.accessToken = data.access_token
      this.clientId = data.client_id
      this.broadcasterId = data.broadcaster_id
      this.userId = data.user_id

      console.log(`Authenticated as ${data.user_name} (${data.user_id})`)
      return { needsAuth: false, user: data }

    } catch (error) {
      console.error('Auth error:', error)
      this.updateStatus('Authentication error')
      return { needsAuth: true, authUrl: `${API_BASE}/twitch/terrarium/auth/login`, error: error.message }
    }
  }

  // Check backend status
  async checkStatus() {
    try {
      const response = await fetch(`${API_BASE}/twitch/terrarium/api/status`)
      return await response.json()
    } catch (error) {
      console.error('Status check failed:', error)
      return { configured: false, authenticated: false }
    }
  }

  // Check if broadcaster exists
  async checkBroadcaster() {
    if (!this.broadcasterLogin) {
      return { exists: false }
    }

    try {
      const response = await fetch(`${API_BASE}/twitch/terrarium/api/broadcaster/${this.broadcasterLogin}`)
      if (!response.ok) {
        return { exists: false }
      }
      const data = await response.json()
      return { exists: true, broadcaster: data }
    } catch (error) {
      console.error('Broadcaster check failed:', error)
      return { exists: false }
    }
  }

  // Connect to Twitch EventSub WebSocket
  async connect() {
    // First authenticate via backend
    const authResult = await this.authenticate()

    if (authResult.needsAuth) {
      this.updateStatus('Authentication required')
      return { needsAuth: true, authUrl: authResult.authUrl }
    }

    // Close any existing WebSocket to prevent duplicate subscriptions
    if (this.ws) {
      this._intentionalClose = true
      this.ws.close()
      this.ws = null
    }

    return new Promise((resolve, reject) => {
      this.updateStatus('Connecting to Twitch EventSub...')

      this.ws = new WebSocket('wss://eventsub.wss.twitch.tv/ws')

      this.ws.onopen = () => {
        console.log('WebSocket connected to Twitch EventSub')
        this.updateStatus('Connected, authenticating...')
      }

      this.ws.onmessage = async (event) => {
        const data = JSON.parse(event.data)
        await this.handleMessage(data)

        if (data.metadata?.message_type === 'session_welcome') {
          this.connected = true
          this.reconnectAttempts = 0
          resolve({ needsAuth: false })
        }
      }

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error)
        this.updateStatus('Connection error')
        reject(error)
      }

      this.ws.onclose = () => {
        console.log('WebSocket closed')
        this.connected = false
        // Only auto-reconnect if the close was unexpected
        if (this._intentionalClose) {
          this._intentionalClose = false
          return
        }
        this.updateStatus('Disconnected')
        this.attemptReconnect()
      }
    })
  }

  // Handle incoming WebSocket messages
  async handleMessage(data) {
    const messageType = data.metadata?.message_type

    switch (messageType) {
      case 'session_welcome':
        this.sessionId = data.payload.session.id
        console.log('Session ID:', this.sessionId)
        this.updateStatus('Subscribing to chat events...')
        await this.subscribeToEvents()
        break

      case 'session_keepalive':
        // Connection is healthy
        break

      case 'notification':
        this.handleNotification(data.payload)
        break

      case 'session_reconnect':
        const reconnectUrl = data.payload.session.reconnect_url
        this.reconnectTo(reconnectUrl)
        break

      case 'revocation':
        console.warn('Subscription revoked:', data.payload)
        break
    }
  }

  // Subscribe to chat messages + bits via EventSub
  async subscribeToEvents() {
    try {
      // Subscribe to channel.chat.message
      await this.createSubscription('channel.chat.message', {
        broadcaster_user_id: this.broadcasterId,
        user_id: this.userId
      })

      // Subscribe to channel.cheer (bits)
      try {
        await this.createSubscription('channel.cheer', {
          broadcaster_user_id: this.broadcasterId
        })
        console.log('Subscribed to cheer (bits) events')
      } catch (cheerError) {
        console.warn('Could not subscribe to cheer events:', cheerError.message)
      }

      this.updateStatus('Connected and listening!')
      console.log('Subscribed to chat events')
    } catch (error) {
      console.error('Failed to subscribe to events:', error)
      this.updateStatus('Subscription failed - ' + error.message)
    }
  }

  // Create an EventSub subscription
  async createSubscription(type, condition) {
    const response = await fetch('https://api.twitch.tv/helix/eventsub/subscriptions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
        'Client-Id': this.clientId,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        type: type,
        version: '1',
        condition: condition,
        transport: {
          method: 'websocket',
          session_id: this.sessionId
        }
      })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(`${error.message || 'Subscription failed'}`)
    }

    return response.json()
  }

  // Handle EventSub notifications
  handleNotification(payload) {
    const event = payload.event
    const subscriptionType = payload.subscription.type

    if (subscriptionType === 'channel.chat.message') {
      const chatMessage = {
        username: event.chatter_user_name,
        userId: event.chatter_user_id,
        message: event.message.text,
        badges: event.badges || [],
        color: event.color || '#9147ff'
      }

      console.log(`Chat: ${chatMessage.username}: ${chatMessage.message}`)
      this.messageCallbacks.forEach(callback => callback(chatMessage))
    }

    if (subscriptionType === 'channel.cheer') {
      const cheerEvent = {
        userId: event.user_id,
        username: event.user_name,
        bits: event.bits,
        message: event.message || '',
        isAnonymous: event.is_anonymous
      }

      console.log(`Cheer: ${cheerEvent.username} cheered ${cheerEvent.bits} bits!`)
      this.cheerCallbacks.forEach(callback => callback(cheerEvent))
    }
  }

  // Register a callback for chat messages
  onMessage(callback) {
    this.messageCallbacks.push(callback)
    return () => {
      this.messageCallbacks = this.messageCallbacks.filter(cb => cb !== callback)
    }
  }

  // Register a callback for cheer (bits) events
  onCheer(callback) {
    this.cheerCallbacks.push(callback)
    return () => {
      this.cheerCallbacks = this.cheerCallbacks.filter(cb => cb !== callback)
    }
  }

  // Register a callback for extension PubSub messages
  onExtensionMessage(callback) {
    this.extensionCallbacks.push(callback)
    return () => {
      this.extensionCallbacks = this.extensionCallbacks.filter(cb => cb !== callback)
    }
  }

  // Fire extension callbacks (called externally from PubSub listener)
  handleExtensionMessage(message) {
    this.extensionCallbacks.forEach(callback => callback(message))
  }

  // Register a callback for status updates
  onStatus(callback) {
    this.statusCallbacks.push(callback)
    return () => {
      this.statusCallbacks = this.statusCallbacks.filter(cb => cb !== callback)
    }
  }

  // Update status
  updateStatus(message) {
    console.log('Status:', message)
    this.statusCallbacks.forEach(callback => callback(message))
  }

  // Check if a user is a Tier 3 subscriber
  async checkSubscription(userId) {
    if (DEBUG_MODE) {
      return { tier: DEBUG_TIER }
    }

    if (this.subscriberCache.has(userId)) {
      const cached = this.subscriberCache.get(userId)
      if (Date.now() - cached.timestamp < 60000) {
        return cached.data
      }
    }

    try {
      const response = await fetch(
        `https://api.twitch.tv/helix/subscriptions/user?broadcaster_id=${this.broadcasterId}&user_id=${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Client-Id': this.clientId
          }
        }
      )

      if (!response.ok) {
        if (response.status === 404) {
          this.subscriberCache.set(userId, { data: null, timestamp: Date.now() })
          return null
        }
        throw new Error(`API error: ${response.status}`)
      }

      const data = await response.json()
      const subscription = data.data?.[0] || null

      this.subscriberCache.set(userId, { data: subscription, timestamp: Date.now() })
      return subscription
    } catch (error) {
      console.error('Failed to check subscription:', error)
      return null
    }
  }

  // Check if user is a moderator or broadcaster
  async checkModerator(userId) {
    if (DEBUG_MODE) return true

    if (userId === this.broadcasterId) {
      return true
    }

    try {
      const response = await fetch(
        `https://api.twitch.tv/helix/moderation/moderators?broadcaster_id=${this.broadcasterId}&user_id=${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Client-Id': this.clientId
          }
        }
      )

      if (!response.ok) return false

      const data = await response.json()
      return data.data?.length > 0
    } catch (error) {
      console.error('Failed to check moderator status:', error)
      return false
    }
  }

  // Check if user is the broadcaster (renamed from original to avoid confusion)
  checkIsBroadcaster(userId) {
    if (DEBUG_MODE) return true
    return userId === this.broadcasterId
  }

  // Reconnect to a new WebSocket URL (Twitch session_reconnect)
  reconnectTo(url) {
    console.log('Reconnecting to:', url)
    const oldWs = this.ws

    this.ws = new WebSocket(url)
    this.ws.onopen = () => {
      // Close old connection without triggering attemptReconnect
      this._intentionalClose = true
      oldWs.close()
      console.log('Reconnected successfully')
    }
    this.ws.onmessage = (event) => this.handleMessage(JSON.parse(event.data))
    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error)
      this.updateStatus('Connection error')
    }
    this.ws.onclose = () => {
      console.log('WebSocket closed')
      this.connected = false
      if (this._intentionalClose) {
        this._intentionalClose = false
        return
      }
      this.updateStatus('Disconnected')
      this.attemptReconnect()
    }
  }

  // Attempt to reconnect after disconnect
  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached')
      this.updateStatus('Connection failed - please refresh')
      return
    }

    this.reconnectAttempts++
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000)

    console.log(`Attempting reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`)
    this.updateStatus(`Reconnecting in ${Math.round(delay / 1000)}s...`)

    setTimeout(() => {
      this.connect().catch(console.error)
    }, delay)
  }

  // Disconnect
  disconnect() {
    if (this.ws) {
      this._intentionalClose = true
      this.ws.close()
      this.ws = null
    }
    this.connected = false
  }

  // Get auth URL for login
  getAuthUrl() {
    return `${API_BASE}/twitch/terrarium/auth/login`
  }

  // Send a chat message
  async sendMessage(message) {
    if (!this.accessToken || !this.broadcasterId) {
      console.error('Cannot send message: not authenticated (token:', !!this.accessToken, 'broadcaster:', !!this.broadcasterId, ')')
      return false
    }

    try {
      const response = await fetch('https://api.twitch.tv/helix/chat/messages', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Client-Id': this.clientId,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          broadcaster_id: this.broadcasterId,
          sender_id: this.userId,
          message: message
        })
      })

      if (!response.ok) {
        const error = await response.json()
        console.error('Failed to send message:', response.status, error.message || error)
        return false
      }

      return true
    } catch (error) {
      console.error('Error sending message:', error)
      return false
    }
  }
}

// Singleton instance
export const twitchApi = new TwitchApiService()